import React, { useState, useEffect } from 'react';
import { apiService } from '../services';
import { Workout } from '../types/workout';
import { Competitor, isIndividualCompetitor, isTeamCompetitor } from '../types/competitor';
import { Athlete } from '../types/athlete';
import { WorkoutResult, CompetitorResult } from '../types/result';
import ResultInputForm from '../component/ResultInputForm';

interface Event {
  id: string;
  name: string;
  competitors: Competitor[];
  workouts: Workout[];
}

const Results: React.FC = () => {
  const [events, setEvents] = useState<Event[]>([]);
  const [selectedEventId, setSelectedEventId] = useState<string>('');
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [athletes, setAthletes] = useState<Athlete[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedWorkoutId, setExpandedWorkoutId] = useState<string | null>(null);
  const [savingWorkoutId, setSavingWorkoutId] = useState<string | null>(null);

  useEffect(() => {
    loadInitialData();
  }, []);

  useEffect(() => {
    if (selectedEventId) {
      const event = events.find(e => e.id === selectedEventId);
      setSelectedEvent(event || null);
    }
  }, [selectedEventId, events]);

  // Only reset expanded workout when event selection changes
  useEffect(() => {
    setExpandedWorkoutId(null);
  }, [selectedEventId]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      const [eventsData, athletesData] = await Promise.all([
        apiService.get<Event[]>('/events'),
        apiService.get<Athlete[]>('/athletes')
      ]);
      setEvents(eventsData);
      setAthletes(athletesData);
    } catch (err: any) {
      console.error('Failed to load data:', err);
    } finally {
      setLoading(false);
    }
  };

  const getAthleteName = (athleteId: string): string => {
    const athlete = athletes.find(a => a.id === athleteId);
    return athlete ? `${athlete.name} ${athlete.lastName}` : 'Unknown Athlete';
  };

  const getCompetitorName = (competitor: Competitor): string => {
    if (isIndividualCompetitor(competitor)) {
      return getAthleteName(competitor.athleteId);
    } else if (isTeamCompetitor(competitor)) {
      return competitor.name;
    }
    return 'Unknown Competitor';
  };

  const getCompetitorResult = (workout: Workout, competitorId: string): WorkoutResult | undefined => {
    const competitorResult = workout.results.find(r => r.competitorId === competitorId);
    return competitorResult?.results[0];
  };

  const handleSaveResult = async (workoutId: string, competitorId: string, result: WorkoutResult) => {
    if (!selectedEvent) return;

    setSavingWorkoutId(workoutId);
    try {
      // Find the workout
      const workout = selectedEvent.workouts.find(w => w.id === workoutId);
      if (!workout) return;

      // Update or create competitor result
      const existingResultIndex = workout.results.findIndex(r => r.competitorId === competitorId);
      let updatedResults: CompetitorResult[];

      if (existingResultIndex >= 0) {
        // Update existing result
        updatedResults = [...workout.results];
        updatedResults[existingResultIndex] = {
          competitorId,
          results: [result]
        };
      } else {
        // Add new result
        updatedResults = [
          ...workout.results,
          {
            competitorId,
            results: [result]
          }
        ];
      }

      // Update the workout with new results
      const updatedWorkout = {
        ...workout,
        results: updatedResults
      };

      // Update all workouts in the event
      const updatedWorkouts = selectedEvent.workouts.map(w =>
        w.id === workoutId ? updatedWorkout : w
      );

      // Send update to backend
      await apiService.put(`/events/${selectedEvent.id}`, {
        workouts: updatedWorkouts
      });

      // Reload events to get fresh data
      const eventsData = await apiService.get<Event[]>('/events');
      setEvents(eventsData);
    } catch (err: any) {
      alert('Failed to save result: ' + err.message);
    } finally {
      setSavingWorkoutId(null);
    }
  };

  const toggleWorkout = (workoutId: string) => {
    setExpandedWorkoutId(expandedWorkoutId === workoutId ? null : workoutId);
  };

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-center items-center py-12">
          <svg className="animate-spin h-12 w-12 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Results</h1>
        <p className="text-gray-600 mt-2">Record workout results for competitors</p>
      </div>

      {/* Event Selector */}
      <div className="mb-8">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Select Event
        </label>
        <select
          value={selectedEventId}
          onChange={(e) => setSelectedEventId(e.target.value)}
          className="w-full max-w-md px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-lg"
        >
          <option value="">-- Choose an event --</option>
          {events.map((event) => (
            <option key={event.id} value={event.id}>
              {event.name}
            </option>
          ))}
        </select>
      </div>

      {/* No Event Selected */}
      {!selectedEvent && !loading && (
        <div className="text-center py-12">
          <svg
            className="mx-auto h-24 w-24 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
          </svg>
          <h3 className="mt-4 text-lg font-semibold text-gray-900">No event selected</h3>
          <p className="mt-2 text-gray-600">Select an event to record results</p>
        </div>
      )}

      {/* Workouts Accordion */}
      {selectedEvent && selectedEvent.workouts.length === 0 && (
        <div className="text-center py-12">
          <svg
            className="mx-auto h-16 w-16 text-gray-400"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
          </svg>
          <h3 className="mt-4 text-lg font-semibold text-gray-900">No workouts</h3>
          <p className="mt-2 text-gray-600">This event has no workouts yet</p>
        </div>
      )}

      {selectedEvent && selectedEvent.workouts.length > 0 && (
        <div className="space-y-4">
          {selectedEvent.workouts.map((workout) => {
            // Filter competitors by workout category
            const eligibleCompetitors = selectedEvent.competitors.filter(
              c => c.category === workout.category
            );

            return (
              <div
                key={workout.id}
                className="border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm"
              >
                {/* Accordion Header */}
                <button
                  onClick={() => toggleWorkout(workout.id)}
                  className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
                >
                  <div className="flex-1 text-left">
                    <h3 className="text-lg font-semibold text-gray-900">{workout.name}</h3>
                    <p className="text-sm text-gray-600 mt-1">{workout.description}</p>
                    <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                      <span className="inline-flex items-center">
                        <svg className="h-4 w-4 mr-1 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                        </svg>
                        {workout.type}
                      </span>
                      <span className="inline-flex items-center">
                        <svg className="h-4 w-4 mr-1 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                        {workout.category}
                      </span>
                      <span className="inline-flex items-center">
                        <svg className="h-4 w-4 mr-1 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                        {eligibleCompetitors.length} competitors
                      </span>
                    </div>
                  </div>
                  <svg
                    className={`h-6 w-6 text-gray-400 transition-transform ${
                      expandedWorkoutId === workout.id ? 'transform rotate-180' : ''
                    }`}
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {/* Accordion Content */}
                {expandedWorkoutId === workout.id && (
                  <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
                    {eligibleCompetitors.length === 0 ? (
                      <p className="text-sm text-gray-600 text-center py-4">
                        No competitors in {workout.category} category
                      </p>
                    ) : (
                      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {eligibleCompetitors.map((competitor) => (
                          <ResultInputForm
                            key={competitor.id}
                            workoutType={workout.type}
                            existingResult={getCompetitorResult(workout, competitor.id)}
                            onSave={(result) => handleSaveResult(workout.id, competitor.id, result)}
                            competitorName={getCompetitorName(competitor)}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default Results;
